import { useMemo } from 'react';
import Highcharts from 'highcharts';
import { CheckCircle } from 'lucide-react';

import { getUserTimezone } from '@/utils/timezone';
import { Chart } from '@/warranty-v3/components/chart';
import type { ConditionViolation } from '@/warranty-v3/entities/condition-violation';
import { conditionViolationColor } from '@/warranty-v3/utils/condition-violation-colors';

export type ConditionViolationsChartProps = {
  conditionViolations: ConditionViolation[];
};

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center rounded-xl border border-green-100 bg-green-50 p-6 text-center text-green-800 shadow-inner">
      <CheckCircle className="mb-3 size-10 text-green-600" />
      <div className="text-lg font-semibold">
        No condition violations detected
      </div>
      <div className="text-sm text-gray-600">
        All systems are operating within warranty conditions.
      </div>
    </div>
  );
}

export function ConditionViolationsChart({
  conditionViolations,
}: ConditionViolationsChartProps) {
  const options = useMemo<Highcharts.Options>(
    function () {
      return {
        chart: {
          type: 'column',
          animation: false,
        },
        time: {
          timezone: getUserTimezone(),
        },
        title: {
          text: 'Condition Violations Over Time',
          style: {
            fontSize: '1.125rem',
          },
        },
        xAxis: {
          type: 'datetime',
          title: { text: 'Time' },
          labels: {
            rotation: -45,
          },
          dateTimeLabelFormats: {
            day: '%b %e, %Y',
            month: '%b %Y',
            year: '%Y',
          },
          gridLineColor: '#e5e7eb',
        },
        yAxis: {
          title: {
            text: 'Count',
          },
          max: null,
          min: 0,
          plotLines: [],
          plotBands: [],
          gridLineColor: '#e5e7eb',
          gridZIndex: -1,
        },
        tooltip: {
          xDateFormat: '%Y-%m-%d %H:%M:%S',
          formatter: undefined,
        },
        plotOptions: {
          series: {
            animation: false, // Disable series animation
            states: {
              inactive: {
                opacity: 1,
              },
            },
          },
          column: { stacking: undefined, softThreshold: false },
        },
        credits: { enabled: false },
        series: conditionViolations.map((violation) => ({
          name: violation.description,
          type: 'column',
          color: conditionViolationColor(violation.id),
          data: violation.historySeries.points.map(
            ([date, affectedUnitsCount]) => [
              date.getTime(),
              affectedUnitsCount,
            ],
          ),
        })),
      };
    },
    [conditionViolations],
  );

  if (conditionViolations.length === 0) {
    return <EmptyState />;
  }

  return <Chart options={options} />;
}
